import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import type { Page } from '@outfalak/shared'
import { api } from '../lib/api.js'
import { useAuth } from '../hooks/useAuth.js'
import { ago, dateTime, initials, money } from '../lib/format.js'
import {
  Badge,
  Card,
  Drawer,
  DrawerSection,
  Empty,
  KeyValue,
  PageHead,
  Pager,
  RowsSkeleton,
  SearchInput,
  Tabs,
} from '../components/ui.js'
import { DownloadIcon, MailIcon, MapPinIcon, PhoneIcon, StoreIcon, UsersIcon } from '../components/icons.js'

const PER_PAGE = 25

/** What `/v1/customers` answers with. */
interface Customer {
  id: string
  name: string | null
  phone: string
  email: string | null
  city: string | null
  verified: boolean
  ordersCount: number
  totalSpent: string | null
  lastOrderAt: string | null
  createdAt: string
}

/**
 * Real customers, from the shop the switcher has selected.
 *
 * There is no RTO risk column, unlike the earlier placeholder: nothing in the
 * platform computes a risk score, and a fabricated low/medium/high is the kind
 * of number a merchant would act on — refusing COD to somebody on the strength
 * of a number the system invented.
 */
export function CustomersPage(): React.ReactNode {
  const { shop } = useAuth()
  const [search, setSearch] = useState('')
  const [verified, setVerified] = useState<'all' | 'yes' | 'no'>('all')
  const [page, setPage] = useState(1)
  const [selected, setSelected] = useState<Customer | null>(null)

  const { data, isPending, error } = useQuery({
    queryKey: ['customers', shop?.id, search, verified, page],
    queryFn: () =>
      api<Page<Customer>>('/v1/customers', {
        query: { q: search, verified: verified === 'all' ? '' : verified, page, perPage: PER_PAGE },
      }),
    enabled: Boolean(shop?.id),
  })

  function reset(next: () => void): void {
    next()
    setPage(1)
  }

  if (!shop) {
    return (
      <Card>
        <Empty icon={<StoreIcon size={24} />} title="No store selected">
          Customers belong to a store. Choose one from the switcher above to see them.
        </Empty>
      </Card>
    )
  }

  const total = data?.total ?? 0
  const pageCount = Math.max(1, Math.ceil(total / PER_PAGE))

  return (
    <>
      <PageHead
        title="Customers"
        subtitle="Everyone who has checked out on this store, with their order history."
        actions={
          <button type="button" className="btn" onClick={() => window.print()}>
            <DownloadIcon size={16} />
            Export
          </button>
        }
      />

      <Card flush>
        <Tabs
          value={verified}
          onChange={(key) => reset(() => setVerified(key))}
          tabs={[
            { key: 'all', label: 'All customers' },
            { key: 'yes', label: 'Phone verified' },
            { key: 'no', label: 'Unverified' },
          ]}
        />

        <div className="toolbar">
          <SearchInput value={search} onChange={(next) => reset(() => setSearch(next))} placeholder="Name, phone or email" />
        </div>

        {error && (
          <Empty error title="Could not load customers">
            {(error as Error).message}
          </Empty>
        )}

        {isPending && <RowsSkeleton rows={8} />}

        {data && data.items.length === 0 && !error && (
          <Empty icon={<UsersIcon size={24} />} title="No customers yet">
            Anyone who completes a checkout on this store will appear here.
          </Empty>
        )}

        {data && data.items.length > 0 && (
          <>
            <div className="table-wrap">
              <table className="table">
                <thead>
                  <tr>
                    <th>Customer</th>
                    <th>City</th>
                    <th className="right">Orders</th>
                    <th className="right">Spent</th>
                    <th>Last order</th>
                    <th>Phone</th>
                  </tr>
                </thead>
                <tbody>
                  {data.items.map((customer) => (
                    <tr key={customer.id} className="is-clickable" onClick={() => setSelected(customer)}>
                      <td>
                        <span className="table__cell">
                          <span className="avatar avatar--sm avatar--muted">{initials(customer.name ?? 'Guest')}</span>
                          <span>
                            {customer.name ?? 'Guest'}
                            <span className="table__sub">{customer.email ?? customer.phone}</span>
                          </span>
                        </span>
                      </td>
                      <td>{customer.city ?? <span className="faint">—</span>}</td>
                      <td className="right num">{customer.ordersCount}</td>
                      {/* Null spend is a customer who has never settled an
                          order — different from ₹0, and shown differently. */}
                      <td className="right num">
                        {customer.totalSpent === null ? <span className="faint">—</span> : money(customer.totalSpent)}
                      </td>
                      <td>
                        {customer.lastOrderAt ? (
                          <>
                            {ago(customer.lastOrderAt)}
                            <span className="table__sub">{dateTime(customer.lastOrderAt).split(',')[0]}</span>
                          </>
                        ) : (
                          <span className="faint">never</span>
                        )}
                      </td>
                      <td>
                        <Badge tone={customer.verified ? 'ok' : 'warn'}>{customer.verified ? 'Verified' : 'Unverified'}</Badge>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <Pager page={page} pageCount={pageCount} total={total} perPage={PER_PAGE} onPage={setPage} noun="customers" />
          </>
        )}
      </Card>

      {selected && (
        <Drawer
          open
          onClose={() => setSelected(null)}
          title={selected.name ?? 'Guest'}
          subtitle={`${selected.ordersCount} order${selected.ordersCount === 1 ? '' : 's'}${
            selected.totalSpent ? ` · ${money(selected.totalSpent)} spent` : ''
          }`}
        >
          <DrawerSection title="Contact">
            <div className="row">
              <span className="avatar avatar--lg">{initials(selected.name ?? 'Guest')}</span>
              <span className="list__text">
                <strong>{selected.name ?? 'Guest'}</strong>
                <small>
                  <PhoneIcon size={12} style={{ verticalAlign: '-2px' }} /> {selected.phone}
                </small>
                {selected.email && (
                  <small>
                    <MailIcon size={12} style={{ verticalAlign: '-2px' }} /> {selected.email}
                  </small>
                )}
              </span>
              <Badge tone={selected.verified ? 'ok' : 'warn'}>{selected.verified ? 'Verified' : 'Unverified'}</Badge>
            </div>
          </DrawerSection>

          <DrawerSection title="Summary">
            <KeyValue
              rows={[
                {
                  label: 'City',
                  value: selected.city ?? '—',
                },
                { label: 'Orders placed', value: selected.ordersCount },
                { label: 'First seen', value: dateTime(selected.createdAt) },
                { label: 'Last order', value: selected.lastOrderAt ? dateTime(selected.lastOrderAt) : 'Never' },
                {
                  label: 'Settled spend',
                  value: selected.totalSpent === null ? '—' : money(selected.totalSpent),
                  kind: 'total',
                },
              ]}
            />
          </DrawerSection>

          {selected.city && (
            <DrawerSection title="Delivers to">
              <p className="row" style={{ alignItems: 'flex-start', fontSize: '.86rem' }}>
                <MapPinIcon size={15} style={{ color: 'var(--ink-faint)', marginTop: 2 }} />
                <span>{selected.city}</span>
              </p>
            </DrawerSection>
          )}
        </Drawer>
      )}
    </>
  )
}
